import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap, catchError, of, map } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';

import { UserService } from '../../../services/user.service';
import { ConversationService } from '../../../services/conversation.service';
import { ChatService } from '../../../services/chat.service';
import { Chat, MessageSearchResult } from '../../../models/message.model';
import { ChatWidget } from '../chat-widget/chat-widget';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { NewConversationDialogComponent } from '../new-conversation-dialog/new-conversation-dialog';
import { UserAvatar } from '../user-avatar/user-avatar';

@Component({
  selector: 'app-messaging-widget',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ChatWidget,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    HighlightPipe,
    UserAvatar,
  ],
  templateUrl: './messaging-widget.html',
  styleUrl: './messaging-widget.css',
})
export class MessagingWidget implements OnInit, OnDestroy {
  isOpen = false;
  /** True only for the one frame a fresh instance mounts already open
   *  (ChatService.widgetOpen$ carried over "open" from a previous page) —
   *  suppresses the widget-panel's height transition so navigating between
   *  pages doesn't replay the open animation. Cleared right after that
   *  first paint, so an actual user toggle still animates normally. */
  skipOpenAnimation = false;
  chats: Chat[] = [];
  filteredChats: Chat[] = [];
  searchQuery = '';
  isSearching = false;
  selectedChat: Chat | null = null;
  /** Whether the open-conversation pane is showing, independent of what
   *  ChatService.selectedChat$ currently holds — that's shared, session-wide
   *  state (e.g. the Messages page may have a conversation open); this flag
   *  is purely local so "back to list" here doesn't deselect it elsewhere. */
  showChatPane = false;
  currentUserId!: number;

  private searchSubject = new Subject<string>();
  private contentMatches: MessageSearchResult[] = [];
  /** Tracks the last chat id synced from ChatService, so a live update to
   *  the same conversation (e.g. an edit) doesn't reopen the pane after the
   *  user has explicitly backed out to the list. Only a genuine change in
   *  which conversation is selected should do that. */
  private lastSyncedChatId: number | null = null;

  @Input() set chatToOpen(chat: Chat | null) {
    if (!chat) return;
    this.chatService.setWidgetOpen(true);
    this.selectChat(chat);
    this.cdr.detectChanges();
  }
  private destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private conversationService: ConversationService,
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.setupSearch();

    let isFirstEmit = true;
    this.chatService.widgetOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      if (isFirstEmit && open) {
        this.skipOpenAnimation = true;
        requestAnimationFrame(() => {
          this.skipOpenAnimation = false;
          this.cdr.detectChanges();
        });
      }
      isFirstEmit = false;

      this.isOpen = open;
      this.cdr.detectChanges();
    });

    const user = this.userService.currentUser;
    if (!user?.id) return;

    this.currentUserId = user.id;
    this.chatService.initializeSocketListeners(this.currentUserId);
    this.chatService.reloadConversations(this.currentUserId);

    this.chatService.conversations$.pipe(takeUntil(this.destroy$)).subscribe((chats) => {
      this.chats = chats;
      if (this.searchQuery.trim()) {
        this.filteredChats = this.getFilteredAndSortedChats(this.searchQuery);
      }
      this.cdr.detectChanges();
    });

    this.chatService.selectedChat$.pipe(takeUntil(this.destroy$)).subscribe((chat) => {
      this.selectedChat = chat;

      if (chat && chat.id !== this.lastSyncedChatId) {
        this.showChatPane = true;
      }
      this.lastSyncedChatId = chat?.id ?? null;

      this.cdr.detectChanges();
    });
  }

  openNewConversationDialog(): void {
    const dialogRef = this.dialog.open(NewConversationDialogComponent, {
      width: '420px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.chatService.setWidgetOpen(true);
      this.chatService.reloadConversations(this.currentUserId);
    });
  }

  private setupSearch(): void {
    this.searchSubject
      .pipe(
        // distinctUntilChanged runs before the debounce so it compares every raw
        // keystroke rather than only the debounced ones. If it ran after, clearing
        // the search and retyping the same term within the debounce window would
        // look like a duplicate and get dropped, leaving the results empty.
        distinctUntilChanged(),
        debounceTime(250),
        switchMap((query) =>
          query.trim()
            ? this.conversationService.searchMessages(this.currentUserId, query).pipe(
                catchError(() => of([] as MessageSearchResult[])),
                map((results) => ({ query, results })),
              )
            : of({ query, results: [] as MessageSearchResult[] }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ query, results }) => {
        this.contentMatches = results;
        this.filteredChats = this.getFilteredAndSortedChats(query);
        this.syncSelectedChatMatches();
        this.isSearching = false;
        this.cdr.detectChanges();
      });
  }

  //keeps the open chat's match details in step with the current query.
  private syncSelectedChatMatches(): void {
    if (!this.selectedChat) return;

    const result = this.contentMatches.find(
      (match) => match.conversation_id === this.selectedChat!.id,
    );

    this.chatService.updateSelectedChatMatches({
      matchCount: result?.matchCount,
      matchingMessageIds: result?.matchingMessageIds,
      matchSnippet: result?.snippet ?? undefined,
    });
  }

  onSearchInput(query: string): void {
    this.isSearching = true;
    this.searchSubject.next(query);
  }

  /**
   * Clears the search box and its results. The empty query is also pushed
   * through the search pipeline so its distinctUntilChanged state is reset —
   * otherwise the next search for the term just cleared would be dropped as a
   * duplicate and silently return nothing.
   * @returns {void}
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.filteredChats = [];
    this.contentMatches = [];
    this.isSearching = false;
    this.searchSubject.next('');
    this.cdr.detectChanges();
  }

  get visibleChats(): Chat[] {
    return this.searchQuery.trim() ? this.filteredChats : this.chats;
  }

  /**
   * Returns true when the full name, or any word in it, starts with the query.
   * @param {string} name - The name to test.
   * @param {string} q - The lower-cased search string.
   * @returns {boolean}
   */
  private nameMatches(name: string, q: string): boolean {
    return (
      name
        .toLowerCase()
        .split(' ')
        .some((word) => word.startsWith(q)) || name.toLowerCase().startsWith(q)
    );
  }
  /**
   * Filters chats by whether any word in the chat name or in a member's name
   * starts with the query, then appends chats whose message content matched on
   * the server. Name matches come first — direct chats before groups, each
   * sorted alphabetically; content matches follow by recency. The current user
   * is skipped so searching your own name does not match every conversation.
   * Returns an empty array for empty queries.
   *
   * Names match by prefix but content matches by substring — searching a
   * phrase like "essay draft" only ever works as a substring.
   * @param {string} query - The search string to filter against.
   * @returns {Chat[]}
   */
  private getFilteredAndSortedChats(query: string): Chat[] {
    if (!query.trim()) return [];

    const q = query.toLowerCase();

    const byName = this.chats
      .filter(
        (chat) =>
          this.nameMatches(chat.name, q) ||
          (chat.members ?? []).some(
            (member) => member.id !== this.currentUserId && this.nameMatches(member.name, q),
          ),
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    const byNameDirect = byName.filter((chat) => !chat.is_group);

    const byNameGroup = byName.filter((chat) => chat.is_group);

    const namedIds = new Set(byName.map((chat) => chat.id));

    const byContent = this.contentMatches
      .filter((result) => !namedIds.has(result.conversation_id))
      .map((result) => this.withMatchDetails(result))
      .filter((chat): chat is Chat => chat !== null)
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

    return [...byNameDirect, ...byNameGroup, ...byContent];
  }

  /**
   * Pairs a server search result with its chat, returning a copy decorated with
   * the match details. Returns null when the chat is not loaded.
   * @param {MessageSearchResult} result - A single content match from the server.
   * @returns {Chat | null}
   */
  private withMatchDetails(result: MessageSearchResult): Chat | null {
    const chat = this.chats.find((c) => c.id === result.conversation_id);
    if (!chat) return null;

    return {
      ...chat,
      matchCount: result.matchCount,
      matchingMessageIds: result.matchingMessageIds,
      matchSnippet: result.snippet ?? undefined,
    };
  }

  toggleWidget(): void {
    this.chatService.setWidgetOpen(!this.isOpen);
  }

  selectChat(chat: Chat): void {
    this.chatService.selectChat(chat, this.currentUserId);
    this.showChatPane = true;
    this.chatService.setWidgetOpen(true);
    this.cdr.detectChanges();
  }

  /**
   * Backs out to the conversation list within the widget only — deliberately
   * does not touch ChatService's selectedChat, so the conversation stays
   * "current" for the Messages page or the next time the widget opens.
   */
  closeChat(): void {
    this.showChatPane = false;
    this.cdr.detectChanges();
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  }

  formatTime(date: Date | string): string {
    return new Date(date).toLocaleDateString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
